import * as React from "react";
import Spinner, { SpinnerSize } from "sh-application/components/common/Spinner";
import StringsStore from "sh-strings/store";
import teamSetupPickerStore from "../store/store";
import TeamSetupPickerUtils from "../TeamSetupPickerUtils";
import { fireAccessibilityAlert } from "sh-application/components/accessibilityAlert";
import { getDurationInMs } from "sh-application/utility/utility";
import { getGenericEventPropertiesObject, logPageView } from "sh-instrumentation";
import { gotoPrevSetupStep, setIsProvisionTeamInProgress } from "../store/store";
import { InstrumentationService, TeamDataService } from "sh-services";
import { observer } from "mobx-react";
import { SetupNavigation } from "../SetupNavigation";
import { TeamSetupPickerStepProps } from "../TeamSetupPickerStepProps";
import { restartApp, UrlFactory } from "sh-application";

const styles = require("./TeamSetupRestoreSchedule.scss");

/**
 * React component for the restore schedule step in the team onboarding flow
 * In this setup step, the user can restore a previously soft-deleted schedule for their MS Team.
 */
@observer
@logPageView("Setup.RestoreSchedule")
export default class TeamSetupRestoreSchedule extends React.Component<TeamSetupPickerStepProps, {}> {
    private _teamSetupPickerStrings: Map<string, string>;
    private _restoreScheduleStrings: Map<string, string>;

    constructor(props: TeamSetupPickerStepProps) {
        super(props);
        this._teamSetupPickerStrings = StringsStore().registeredStringModules.get("teamSetupPicker").strings;
        this._restoreScheduleStrings = StringsStore().registeredStringModules.get("restoreSchedule").strings;

        this.instrumentLoadScreen();
    }

    componentDidMount() {
        // Fire accessibility alert because the heading content doesn't get read via focus.
        if (teamSetupPickerStore.teamSetupInfo && teamSetupPickerStore.teamSetupInfo.msTeamInfo) {
            const restoreScheduleDialogAriaAlert: string = this._restoreScheduleStrings.get("restoreScheduleDialogAriaAlert").format(teamSetupPickerStore.teamSetupInfo.msTeamInfo.teamName);
            fireAccessibilityAlert(restoreScheduleDialogAriaAlert);
        }
    }

    /**
     * Instrument load of this view
     */
    private instrumentLoadScreen() {
        InstrumentationService.logEvent(
            this.getInstrumentationEventName(),
            [getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.RestoreScheduleViewed)]);
    }

    /**
     * Handler for Restore button click
     */
    private handleRestoreClicked = () => {
        InstrumentationService.logEvent(
            this.getInstrumentationEventName(),
            [getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.ConfirmRestoreSchedule)]);

        this.restoreScheduleTeam();
    }

    /**
     * Handler for Back button click
     */
    private handleBackClicked = () => {
        gotoPrevSetupStep();
    }

    /**
     * Restore a previously soft-deleted schedule team
     */
    private async restoreScheduleTeam() {
        const scheduleTeamInfo = teamSetupPickerStore.teamSetupInfo.scheduleTeamInfo;

        if (!scheduleTeamInfo || !scheduleTeamInfo.isSoftDeleted) {
            // No schedule to restore, this shouldn't happen
            TeamSetupPickerUtils.handleStaffHubServiceError(new Error("No soft-deleted schedule found"));
            return;
        }

        let restoredScheduleTeamId: string = null;
        setIsProvisionTeamInProgress(true /* isProvisioning */);

        let restoreDuration: number = 0;
        try {
            const startTimeStamp: number = InstrumentationService.getCurrentTimeStamp();
            fireAccessibilityAlert(this._teamSetupPickerStrings.get("restoreTeamStartingAriaMessage"));

            // Call the restore API
            restoredScheduleTeamId = scheduleTeamInfo.id;
            await TeamDataService.restoreSchedule(scheduleTeamInfo.id, scheduleTeamInfo.eTag);

            const endTimeStamp: number = InstrumentationService.getCurrentTimeStamp();

            if (restoredScheduleTeamId) {
                restoreDuration = getDurationInMs(startTimeStamp, endTimeStamp);

                // Navigate to the restored team
                window.location.href = UrlFactory.getSchedulesUrl(restoredScheduleTeamId);

                this.props.onClose();
            }
        } catch (error) {
            TeamSetupPickerUtils.handleStaffHubServiceError(error);
        } finally {
            // Stop the progress indicator only if the restore failed.
            // If it succeeded, continue to display the progress indicator so that there is no rendering flashing when switching to the team.
            if (!restoredScheduleTeamId) {
                setIsProvisionTeamInProgress(false /* isProvisioning */);
            }

            InstrumentationService.logEvent(
                this.getInstrumentationEventName(),
                [
                    getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.ScheduleTeamRestored),
                    getGenericEventPropertiesObject(InstrumentationService.properties.RestoreScheduleTeamSuccess, !!restoredScheduleTeamId),
                    getGenericEventPropertiesObject(InstrumentationService.properties.Duration, restoreDuration)
                ]);
            this.setFocusOnClose();
            restartApp();
        }
    }

    private getInstrumentationEventName(): string {
        return TeamSetupPickerUtils.getInstrumentationEventNameForProvisionScheduleTeam(this.props.isOnboarding);
    }

    /**
     * Renders progress indicator
     */
    private renderProgressIndicator(): JSX.Element {
        return (
            <div className={ styles.progressIndicatorContainer }>
                <Spinner className={ styles.spinner } size={ SpinnerSize.small } label={ this._teamSetupPickerStrings.get("restoreTeamProgressIndicatorMessage") } />
            </div>
        );
    }

    private setFocusOnClose(): void {
        // Intentionally empty for now, mirrors timezone picker
    }

    render() {
        const teamName = teamSetupPickerStore.teamSetupInfo && teamSetupPickerStore.teamSetupInfo.msTeamInfo
            ? teamSetupPickerStore.teamSetupInfo.msTeamInfo.teamName
            : "";

        return (
            <div className={ styles.restoreScheduleContent }>
                <div className={ styles.content }>
                    <div className={ styles.header }>
                        <h1 className={ styles.title }>
                            {this._restoreScheduleStrings.get("restoreScheduleTitle").format(teamName)}
                        </h1>
                        <p className={ styles.description }>
                            {this._restoreScheduleStrings.get("restoreScheduleDescription")}
                        </p>
                    </div>

                    {teamSetupPickerStore.isProvisionTeamInProgress && this.renderProgressIndicator()}
                </div>

                {!teamSetupPickerStore.isProvisionTeamInProgress &&
                    <SetupNavigation
                        onOk={ this.handleRestoreClicked }
                        okButtonLabel={ this._restoreScheduleStrings.get("restoreScheduleButtonLabel") }
                        onCancel={ this.handleBackClicked }
                        cancelButtonLabel={ this._teamSetupPickerStrings.get("backButtonLabel") }
                        showStepIndicator={ teamSetupPickerStore.showStepIndicator }
                        currentStepIndex={ teamSetupPickerStore.currentSetupStepIndex }
                        stepIndicatorSize={ teamSetupPickerStore.setupSequence && teamSetupPickerStore.setupSequence.length }
                    />
                }
            </div>
        );
    }
}
